import React, { useState } from "react";
import { Form, Input, Button, message } from "antd";
import { useNavigate } from "react-router-dom";
import { MailOutlined, LockOutlined } from "@ant-design/icons";
import { loginUser } from "../../ApiServices/SignInApi";
import "./SignIn.css";
import { jwtDecode } from "jwt-decode";
import Logo from "../../Components/Logo/Logo";
import LoginFlag from "../../Pages/LoginFlag/LoginFlag";

function SignIn({ setLoggedIn, deviceId, userIp, setProfileImage }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [securityData, setSecurityData] = useState(null);
  const [isPhoneLogin, setIsPhoneLogin] = useState(false);

  // ✅ Central phone normalizer
  const normalizePhoneNumber = (input) => {
    let digits = input.replace(/\D/g, "");
    if (digits.startsWith("2550")) {
      digits = "255" + digits.slice(4);
    } else if (digits.startsWith("0")) {
      digits = "255" + digits.slice(1);
    } else if (!digits.startsWith("255")) {
      digits = "255" + digits;
    }
    return digits.slice(0, 12);
  };

  const handleSignin = async (values) => {
    setLoading(true);
    try {
      let username;
      const rawInput = values.email.trim();

      if (/^\d+$/.test(rawInput)) {
        username = normalizePhoneNumber(rawInput);
        setIsPhoneLogin(true);
      } else {
        username = rawInput;
        setIsPhoneLogin(false);
      }

      const credentials = {
        username,
        password: values.password,
      };

      const response = await loginUser(credentials, deviceId, userIp);
      if (response.code === 200) {
        handleSuccessfulLogin(response);
      } else if (response.code === 205) {
        handleSecurityCheck(response, username);
      } else if (response.code === 423) {
        handleAccountLocked(response, username);
      } else if (response.code === 412) {
        message.error("Invalid password, please try again");
      } else {
        message.error(response.message || "Login failed");
      }
    } catch (error) {
      handleLoginError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessfulLogin = (response) => { 
    const { jwt: token, photo } = response.data;
    const decodedToken = jwtDecode(token);

    localStorage.setItem("accessToken", token);
    localStorage.setItem("userInfo", JSON.stringify({
      name: decodedToken.name,
      username: decodedToken.username,
      role: decodedToken.role,
      exp: decodedToken.exp,
      userId: decodedToken.userId,
    }));
    
    setLoggedIn(true);
    message.success("Login successful!");
    redirectUser(decodedToken.role.toLowerCase(), decodedToken);
  };

  const redirectUser = (role, userData) => {
    const routes = {
      admin: "/admin-page/dashboard",
      teacher: {
        path: "/teacher-home",
        state: { userId: userData.userId, name: userData.name },
      },
      default: "/courses",
    };

    if (role === "teacher") {
      navigate(routes.teacher.path, { state: routes.teacher.state });
    } else {
      navigate(routes[role] || routes.default);
    }
  };

  const handleSecurityCheck = (response, username) => {
    setSecurityData({
      message: response.message,
      email: isPhoneLogin ? undefined : username,
      phoneNumber: isPhoneLogin ? username : undefined,
      deviceId,
      userIp,
    });
    setShowSecurityModal(true);
  };

  const handleAccountLocked = (response, username) => {
    message.warning(response.message || "Account needs verification");
    navigate("/verify-otp", {
      state: {
        email: isPhoneLogin ? undefined : username,
        phoneNumber: isPhoneLogin ? username : undefined,
        deviceId,
        userIp,
      },
    });
  };

  const handleLoginError = (error) => {
    console.error("Login error:", error);
    const errorMessage =
      error.response?.message ||
      error.message ||
      "An error occurred during login";
    message.error(errorMessage);
  };

  const handleReset = () => navigate("/reset");

  const handleVerifySuccess = () => {
    setShowSecurityModal(false);
    message.success("Account verified successfully! You can now login.");
  };

  const validateUsername = (_, value) => {
    if (!value) {
      return Promise.reject("Please enter your email or phone number");
    }

    const digits = value.replace(/\D/g, "");

    if (/^\d+$/.test(value)) {
      if (
        (digits.startsWith("255") && digits.length !== 12) ||
        (digits.startsWith("0") && digits.length !== 10) ||
        (!digits.startsWith("255") && !digits.startsWith("0") && digits.length !== 9)
      ) {
        return Promise.reject("Enter a valid phone");
      }
      return Promise.resolve();
    }

    if (value.trim() !== value) {
      return Promise.reject("Remove spaces from email");
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return Promise.reject("Enter a valid email");
    }

    return Promise.resolve();
  };

  return (
    <div className="signin-page-container">
      <div className="signin-container">
        <Logo />
        <h4>Sign in to your account</h4>
        <span className="signin-text">Welcome Back! Login with your credentials</span>

        <Form className="signin-form" layout="vertical" onFinish={handleSignin}>
          <Form.Item
            name="email"
            rules={[
              { validator: validateUsername },
            ]}
            normalize={(value) => value.trim()}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="Email or Phone Number"
              className="signin-input"
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: "Please enter your password" },
              { min: 4, message: "Password must be at least 4 characters" },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Password"
              className="signin-input"
            />
          </Form.Item>

          <span onClick={handleReset} className="signin-reset-password">
            <span className="forget-password">Forgot your password? </span> Reset Password
          </span>

          <Button
            type="primary"
            htmlType="submit"
            className="signin-button"
            loading={loading}
            disabled={loading}
          >
            {loading ? "Signing In..." : "Sign In"}
          </Button>
        </Form>

        <p className="signin-link">
          Don't have an account? <a href="/signUp">Sign Up</a>
        </p>

        {showSecurityModal && (
          <div className="security-modal-overlay">
            <div className="security-modal-content">
              <LoginFlag
                email={securityData?.email}
                deviceId={deviceId}
                userIp={userIp}
                userId={securityData?.userId}
                setLoggedIn={setLoggedIn}
                onVerificationSuccess={handleVerifySuccess}
                onClose={() => setShowSecurityModal(false)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SignIn;
